/**
 * TPCCMeasureDetailPanel Component
 * 
 * Specialized detail view for TPCC (Total Per Capita Cost) measure showing:
 * - Key metrics (decile, patient count, avg cost, benchmark, avg risk percentile)
 * - Insights (positioned right after metrics for visibility)
 * - Cost breakdown by category + Chronic Conditions & Risk Distribution
 * - Utilization Patterns (matching spec format)
 * - High-cost patients table (filterable by condition, risk, utilization)
 * 
 * @see MIPS_Cost_Dashboard_Implementation_Spec_v2_2
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft,
  Users,
  DollarSign,
  Activity,
  TrendingUp,
  Eye,
  Lightbulb,
  X,
} from 'lucide-react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';
import { PatientDetailModal } from './PatientDetailModal';
import { DecileBar, ScoreSubmissionOnlyView } from './CostSharedComponents';
import { type CostMeasure } from '@/lib/cost-calculator-utils';

// ============================================
// Types
// ============================================

interface TPCCMeasureDetailPanelProps {
  measure: CostMeasure;
  practiceId: string;
  npi?: string;
  onClose: () => void;
}

interface TPCCDetailData {
  metrics: {
    decile: number | null;
    patientCount: number;
    avgCost: number;
    nationalAvg: number;
    variance: number;
    avgRisk: number;
  };
  costBreakdown: {
    key: string;
    label: string;
    totalCost: number;
    patientCount: number;
    pctOfTotal: number;
  }[];
  conditions: {
    key: string;
    label: string;
    patientCount: number;
    pctOfPanel: number;
    totalCost: number;
    avgCost: number;
  }[];
  riskDistribution: {
    key: string;
    label: string;
    patientCount: number;
    pctOfPatients: number;
    totalCost: number;
    pctOfCost: number;
  }[];
  utilizationPatterns: {
    key: string;
    label: string;
    patientCount: number;
    totalCost: number;
    pctOfSpend: number;
  }[];
  utilizationFlags: {
    key: string;
    label: string;
    patientCount: number;
    pctOfPanel: number;
  }[];
  insights: {
    id: string;
    priority: number;
    text: string;
  }[];
  highCostPatients: {
    mbi: string;
    gender: string;
    dob: string;
    dateOfDeath: string | null;
    totalCost: number;
    nationalAvg: number;
    variance: number;
    riskPercentile: number;
    conditions: string;
    conditionCount: number;
    topCategory: string;
    topCategoryPct: number;
    hasHospitalization: boolean;
    hasPostAcute: boolean;
    hasED: boolean;
    hasHospice: boolean;
    utilizationPattern: string;
  }[];
}

interface ActiveFilter {
  type: 'condition' | 'risk' | 'utilization' | 'utilizationFlag';
  value: string;
  label: string;
}

interface QuadrantFilter {
  value: string;
  label: string;
}

interface UtilizationFilter {
  type: 'utilization' | 'utilizationFlag';
  value: string;
  label: string;
}

// ============================================
// Helper Functions
// ============================================

function formatCurrency(value: number): string {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatPercentInt(value: number): string {
  return `${Math.round(value)}%`;
}

function calculateAge(dob: string | null): number | null {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

// ============================================
// Component
// ============================================

export function TPCCMeasureDetailPanel({
  measure,
  practiceId,
  npi,
  onClose,
}: TPCCMeasureDetailPanelProps) {
  const [data, setData] = useState<TPCCDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAllPatients, setShowAllPatients] = useState(false);
  const [selectedPatientMbi, setSelectedPatientMbi] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter | null>(null);
  const [quadrantFilter, setQuadrantFilter] = useState<QuadrantFilter | null>(null);
  const [utilizationFilter, setUtilizationFilter] = useState<UtilizationFilter | null>(null);
  const [utilizationView, setUtilizationView] = useState<'cost' | 'patient'>('patient');

  // Load TPCC detail data (skip when score-only — no patient-level feedback files)
  useEffect(() => {
    if (measure.scoreSource === 'cms_submission_only') {
      setIsLoading(false);
      return;
    }
    loadTPCCDetail();
  }, [practiceId, npi, measure.scoreSource]);

  const loadTPCCDetail = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const headers = { Authorization: `Bearer ${token}` };

      const url = new URL(`${apiUrl}/api/practices/${practiceId}/cost/tpcc/detail`);
      if (npi) url.searchParams.set('npi', npi);

      const response = await fetch(url.toString(), { headers });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (err) {
      console.error('Error loading TPCC detail:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (measure.scoreSource === 'cms_submission_only') {
    return <ScoreSubmissionOnlyView measure={measure} onClose={onClose} />;
  }

  // Filter handlers
  const handleConditionClick = (key: string, label: string) => {
    // Use label as value since p.conditions contains human-readable names
    if (activeFilter?.type === 'condition' && activeFilter.value === label) {
      setActiveFilter(null);
    } else {
      setActiveFilter({ type: 'condition', value: label, label });
    }
  };

  const handleRiskClick = (key: string, label: string) => {
    if (activeFilter?.type === 'risk' && activeFilter.value === key) {
      setActiveFilter(null);
    } else {
      setActiveFilter({ type: 'risk', value: key, label });
    }
  };

  const handleUtilizationClick = (key: string, label: string) => {
    if (utilizationFilter?.type === 'utilization' && utilizationFilter.value === key) {
      setUtilizationFilter(null);
    } else {
      setUtilizationFilter({ type: 'utilization', value: key, label });
    }
  };

  // Handler for Patient View (inclusive filtering by flag)
  const handleUtilizationFlagClick = (key: string, label: string) => {
    if (utilizationFilter?.type === 'utilizationFlag' && utilizationFilter.value === key) {
      setUtilizationFilter(null);
    } else {
      setUtilizationFilter({ type: 'utilizationFlag', value: key, label });
    }
  };

  // Handler for Quadrant chart click
  const handleQuadrantClick = (quadrant: string, label: string) => {
    if (quadrantFilter?.value === quadrant) {
      setQuadrantFilter(null);
    } else {
      setQuadrantFilter({ value: quadrant, label });
    }
  };

  const clearFilter = () => {
    setActiveFilter(null);
  };

  const clearQuadrantFilter = () => {
    setQuadrantFilter(null);
  };

  const clearUtilizationFilter = () => {
    setUtilizationFilter(null);
  };

  const clearAllFilters = () => {
    setActiveFilter(null);
    setQuadrantFilter(null);
    setUtilizationFilter(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No TPCC data available</p>
        <Button variant="outline" className="mt-4" onClick={onClose}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Measures
        </Button>
      </div>
    );
  }

  const { metrics, costBreakdown, conditions, riskDistribution, utilizationPatterns, utilizationFlags, insights, highCostPatients } = data;

  // Calculate cost percentile for each patient (for quadrant scatter plot)
  const sortedByCost = [...highCostPatients].sort((a, b) => a.totalCost - b.totalCost);
  const costPercentileMap = new Map<string, number>();
  sortedByCost.forEach((p, idx) => {
    // Percentile: what percent of patients have cost <= this patient's cost
    const percentile = ((idx + 1) / sortedByCost.length) * 100;
    costPercentileMap.set(p.mbi, percentile);
  });

  // Calculate 95th percentile cost for capped dollar view
  const p95Index = Math.floor(sortedByCost.length * 0.95);
  const cost95thPercentile = sortedByCost.length > 0 ? sortedByCost[p95Index]?.totalCost || sortedByCost[sortedByCost.length - 1].totalCost : 100000;
  const medianCostDollar = sortedByCost.length > 0 ? sortedByCost[Math.floor(sortedByCost.length / 2)].totalCost : metrics.avgCost;
  const maxCostForChart = cost95thPercentile * 1.05; // Add 5% buffer
  const outliersAboveCap = highCostPatients.filter(p => p.totalCost > cost95thPercentile).length;

  // Calculate linear regression for expected cost trend line
  // y = mx + b where x = risk percentile, y = cost
  const n = highCostPatients.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
  highCostPatients.forEach(p => {
    sumX += p.riskPercentile;
    sumY += p.totalCost;
    sumXY += p.riskPercentile * p.totalCost;
    sumX2 += p.riskPercentile * p.riskPercentile;
  });
  
  const slope = n > 1 ? (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX) : 0;
  const intercept = n > 0 ? (sumY - slope * sumX) / n : 0;
  
  // Expected cost at any risk percentile
  const getExpectedCost = (riskPct: number) => Math.max(0, intercept + slope * riskPct);
  
  // Trend line endpoints for chart
  const trendLineStart = { x: 0, y: getExpectedCost(0) };
  const trendLineEnd = { x: 100, y: Math.min(getExpectedCost(100), maxCostForChart) };

  // Create scatter data with cost percentile and cost ratio
  const scatterData = highCostPatients.map(p => {
    const expectedCost = getExpectedCost(p.riskPercentile);
    const costRatio = expectedCost > 0 ? p.totalCost / expectedCost : 1;
    return {
      ...p,
      costPercentile: costPercentileMap.get(p.mbi) || 0,
      // For dollar view, cap the display cost at 95th percentile
      displayCost: Math.min(p.totalCost, maxCostForChart),
      expectedCost,
      costRatio,
    };
  });
  
  // Count flagged patients (>2x expected)
  const flaggedCount = scatterData.filter(p => p.costRatio > 2.0).length;

  // Quadrant thresholds at 50% for both axes (percentile view)
  const riskThreshold = 50;
  const costThreshold = 50;
  
  // Median risk for dollar view
  const sortedByRisk = [...highCostPatients].sort((a, b) => a.riskPercentile - b.riskPercentile);
  const medianRiskPercentile = sortedByRisk.length > 0 ? sortedByRisk[Math.floor(sortedByRisk.length / 2)].riskPercentile : 50;

  // Assign quadrant to each patient (using percentiles)
  const getQuadrant = (riskPct: number, costPct: number) => {
    const isHighRisk = riskPct >= riskThreshold;
    const isHighCost = costPct >= costThreshold;
    if (!isHighRisk && isHighCost) return 'opportunity';    // Low Risk, High Cost
    if (isHighRisk && isHighCost) return 'expected';        // High Risk, High Cost
    if (!isHighRisk && !isHighCost) return 'efficient';     // Low Risk, Low Cost
    return 'wellManaged';                                    // High Risk, Low Cost
  };

  // Helper: check if patient matches utilization filter
  const matchesUtilizationFilter = (p: typeof highCostPatients[0]) => {
    if (!utilizationFilter) return true;
    if (utilizationFilter.type === 'utilization') {
      return p.utilizationPattern === utilizationFilter.value;
    }
    if (utilizationFilter.type === 'utilizationFlag') {
      if (utilizationFilter.value === 'hospitalization') return p.hasHospitalization;
      if (utilizationFilter.value === 'postAcute') return p.hasPostAcute;
      if (utilizationFilter.value === 'ed') return p.hasED;
      if (utilizationFilter.value === 'hospice') return p.hasHospice;
      if (utilizationFilter.value === 'outpatientOnly') return !p.hasHospitalization && !p.hasPostAcute && !p.hasED && !p.hasHospice;
    }
    return true;
  };

  // Helper: check if patient matches quadrant filter
  const matchesQuadrantFilter = (p: typeof highCostPatients[0]) => {
    if (!quadrantFilter) return true;
    const costPct = costPercentileMap.get(p.mbi) || 0;
    return getQuadrant(p.riskPercentile, costPct) === quadrantFilter.value;
  };

  // Helper: check if patient matches condition/risk filter (activeFilter)
  const matchesConditionRiskFilter = (p: typeof highCostPatients[0]) => {
    if (!activeFilter) return true;
    if (activeFilter.type === 'condition') {
      return p.conditions?.toLowerCase().includes(activeFilter.value.toLowerCase());
    }
    if (activeFilter.type === 'risk') {
      if (activeFilter.value === 'high') return p.riskPercentile > 75;
      if (activeFilter.value === 'medium') return p.riskPercentile >= 25 && p.riskPercentile <= 75;
      if (activeFilter.value === 'low') return p.riskPercentile < 25;
    }
    return true;
  };

  // Patients for scatter chart (filtered by utilization + condition/risk)
  const patientsForQuadrantChart = highCostPatients.filter(p => matchesUtilizationFilter(p) && matchesConditionRiskFilter(p));
  const scatterDataFiltered = scatterData.filter(p => matchesUtilizationFilter(p) && matchesConditionRiskFilter(p));

  // Patients for utilization chart (filtered by quadrant + condition/risk)
  const patientsForUtilizationChart = highCostPatients.filter(p => matchesQuadrantFilter(p) && matchesConditionRiskFilter(p));

  // Patients for condition/risk tables (filtered by quadrant + utilization)
  const patientsForConditionRiskTables = highCostPatients.filter(p => matchesQuadrantFilter(p) && matchesUtilizationFilter(p));

  // Synced chronic conditions based on quadrant + utilization filters
  // Use cond.label for matching since p.conditions contains human-readable names
  const syncedConditions = conditions.map(cond => {
    const matchingPatients = patientsForConditionRiskTables.filter(p => 
      p.conditions?.toLowerCase().includes(cond.label.toLowerCase())
    );
    const totalCost = matchingPatients.reduce((sum, p) => sum + p.totalCost, 0);
    return {
      ...cond,
      patientCount: matchingPatients.length,
      pctOfPanel: patientsForConditionRiskTables.length > 0 ? (matchingPatients.length / patientsForConditionRiskTables.length) * 100 : 0,
      totalCost,
    };
  });

  // Synced risk distribution based on quadrant + utilization filters
  const syncedRiskDistribution = riskDistribution.map(r => {
    let matchingPatients: typeof patientsForConditionRiskTables = [];
    if (r.key === 'high') matchingPatients = patientsForConditionRiskTables.filter(p => p.riskPercentile > 75);
    else if (r.key === 'medium') matchingPatients = patientsForConditionRiskTables.filter(p => p.riskPercentile >= 25 && p.riskPercentile <= 75);
    else if (r.key === 'low') matchingPatients = patientsForConditionRiskTables.filter(p => p.riskPercentile < 25);
    
    const totalCost = matchingPatients.reduce((sum, p) => sum + p.totalCost, 0);
    const totalCostAll = patientsForConditionRiskTables.reduce((sum, p) => sum + p.totalCost, 0);
    
    return {
      ...r,
      patientCount: matchingPatients.length,
      pctOfPatients: patientsForConditionRiskTables.length > 0 ? (matchingPatients.length / patientsForConditionRiskTables.length) * 100 : 0,
      totalCost,
      pctOfCost: totalCostAll > 0 ? (totalCost / totalCostAll) * 100 : 0,
    };
  });

  // Calculate quadrant stats based on utilization-filtered patients
  const quadrantStats = {
    opportunity: { count: 0, totalCost: 0, label: 'Low Risk / High Cost', color: 'bg-red-100 border-red-300 text-red-800' },
    expected: { count: 0, totalCost: 0, label: 'High Risk / High Cost', color: 'bg-amber-100 border-amber-300 text-amber-800' },
    efficient: { count: 0, totalCost: 0, label: 'Low Risk / Low Cost', color: 'bg-gray-100 border-gray-300 text-gray-700' },
    wellManaged: { count: 0, totalCost: 0, label: 'High Risk / Low Cost', color: 'bg-green-100 border-green-300 text-green-800' },
  };
  
  scatterDataFiltered.forEach(p => {
    const q = getQuadrant(p.riskPercentile, p.costPercentile);
    quadrantStats[q as keyof typeof quadrantStats].count++;
    quadrantStats[q as keyof typeof quadrantStats].totalCost += p.totalCost;
  });

  // Calculate utilization stats based on quadrant-filtered patients
  const syncedUtilizationPatterns = utilizationPatterns.map(pattern => {
    const matchingPatients = patientsForUtilizationChart.filter(p => p.utilizationPattern === pattern.key);
    const totalCost = matchingPatients.reduce((sum, p) => sum + p.totalCost, 0);
    return {
      ...pattern,
      // Rename "Outpatient" to "Outpatient Services" for consistency
      label: pattern.key === 'outpatient' ? 'Outpatient Services' : pattern.label,
      patientCount: matchingPatients.length,
      totalCost,
      pctOfSpend: patientsForUtilizationChart.length > 0 
        ? (totalCost / patientsForUtilizationChart.reduce((sum, p) => sum + p.totalCost, 0)) * 100 
        : 0,
    };
  });

  const syncedUtilizationFlags = (() => {
    const baseFlags = utilizationFlags?.map(flag => {
      let matchingCount = 0;
      if (flag.key === 'hospitalization') matchingCount = patientsForUtilizationChart.filter(p => p.hasHospitalization).length;
      else if (flag.key === 'postAcute') matchingCount = patientsForUtilizationChart.filter(p => p.hasPostAcute).length;
      else if (flag.key === 'ed') matchingCount = patientsForUtilizationChart.filter(p => p.hasED).length;
      else if (flag.key === 'hospice') matchingCount = patientsForUtilizationChart.filter(p => p.hasHospice).length;
      return {
        ...flag,
        patientCount: matchingCount,
        pctOfPanel: patientsForUtilizationChart.length > 0 ? (matchingCount / patientsForUtilizationChart.length) * 100 : 0,
      };
    }) || [];
    
    // Add "Outpatient Services" for patients without any high-acuity flags
    const outpatientOnlyCount = patientsForUtilizationChart.filter(
      p => !p.hasHospitalization && !p.hasPostAcute && !p.hasED && !p.hasHospice
    ).length;
    
    return [
      ...baseFlags,
      {
        key: 'outpatientOnly',
        label: 'Outpatient Services',
        patientCount: outpatientOnlyCount,
        pctOfPanel: patientsForUtilizationChart.length > 0 ? (outpatientOnlyCount / patientsForUtilizationChart.length) * 100 : 0,
      }
    ];
  })();

  // Apply all filters (compound filtering - AND logic)
  const filteredPatients = highCostPatients.filter(p => {
    // Apply condition/risk filter (from sidebar tables)
    if (activeFilter) {
      if (activeFilter.type === 'condition') {
        if (!p.conditions?.toLowerCase().includes(activeFilter.value.toLowerCase())) return false;
      }
      if (activeFilter.type === 'risk') {
        if (activeFilter.value === 'high' && p.riskPercentile <= 75) return false;
        if (activeFilter.value === 'medium' && (p.riskPercentile < 25 || p.riskPercentile > 75)) return false;
        if (activeFilter.value === 'low' && p.riskPercentile >= 25) return false;
      }
    }
    
    // Apply quadrant filter
    if (quadrantFilter) {
      const costPct = costPercentileMap.get(p.mbi) || 0;
      if (getQuadrant(p.riskPercentile, costPct) !== quadrantFilter.value) return false;
    }
    
    // Apply utilization filter
    if (utilizationFilter) {
      if (utilizationFilter.type === 'utilization') {
        // Cost view: exclusive assignment
        if (p.utilizationPattern !== utilizationFilter.value) return false;
      }
      if (utilizationFilter.type === 'utilizationFlag') {
        // Patient view: inclusive flags
        if (utilizationFilter.value === 'hospitalization' && !p.hasHospitalization) return false;
        if (utilizationFilter.value === 'postAcute' && !p.hasPostAcute) return false;
        if (utilizationFilter.value === 'ed' && !p.hasED) return false;
        if (utilizationFilter.value === 'hospice' && !p.hasHospice) return false;
        if (utilizationFilter.value === 'outpatientOnly' && (p.hasHospitalization || p.hasPostAcute || p.hasED || p.hasHospice)) return false;
      }
    }
    
    return true;
  });

  const displayedPatients = showAllPatients ? filteredPatients : filteredPatients.slice(0, 10);

  // Calculate max cost for bar scaling
  const maxCost = Math.max(...costBreakdown.map(c => c.totalCost));
  const maxUtilCost = Math.max(...syncedUtilizationPatterns.map(u => u.totalCost), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Button variant="ghost" size="sm" onClick={onClose} className="mb-2 -ml-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Measures
          </Button>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold">Total Per Capita Cost (TPCC)</h2>
            <Badge className="bg-blue-100 text-blue-800">Population</Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Per-beneficiary annual cost for attributed Medicare FFS population
          </p>
        </div>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-5 gap-4">
        {/* Decile Score */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Decile Score</p>
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold">
              {metrics.decile?.toFixed(1) ?? measure.points?.toFixed(1) ?? '—'}
            </span>
          </div>
          <div className="mt-2">
            <DecileBar decile={metrics.decile ?? measure.points ?? 1} />
          </div>
        </div>

        {/* Beneficiaries */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Beneficiaries</p>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-muted-foreground" />
            <span className="text-3xl font-bold">{metrics.patientCount.toLocaleString()}</span>
          </div>
        </div>

        {/* Your Avg Cost */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Your Avg Cost</p>
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-muted-foreground" />
            <span className="text-3xl font-bold">{formatCurrency(metrics.avgCost)}</span>
          </div>
          <p className={`text-xs mt-1 ${metrics.variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {metrics.variance > 0 ? '+' : ''}{formatPercent(metrics.variance)} vs benchmark
          </p>
        </div>

        {/* Benchmark */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Benchmark</p>
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-muted-foreground" />
            <span className="text-3xl font-bold">{formatCurrency(metrics.nationalAvg)}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">National Median</p>
        </div>

        {/* Avg Risk Percentile */}
        <div className="bg-card border rounded-lg p-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Avg Risk %ile</p>
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-muted-foreground" />
            <span className="text-3xl font-bold">{metrics.avgRisk}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">HCC Risk Rank</p>
        </div>
      </div>

      {/* Insights Row - Right after metrics */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb className="h-5 w-5 text-amber-500" />
          <h3 className="font-semibold">Insights</h3>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {insights.length > 0 ? (
            insights.map((insight) => (
              <div key={insight.id} className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-900">{insight.text}</p>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground col-span-3">No actionable insights at this time.</p>
          )}
        </div>
      </div>

      {/* Cost by Category + Chronic Conditions & Risk Distribution */}
      <div className="grid grid-cols-2 gap-6">
        {/* Cost by Category - Horizontal Bars */}
        <div className="bg-card border rounded-lg p-4">
          <h3 className="font-semibold mb-4 uppercase text-sm tracking-wide">Cost by Category</h3>
          
          <div className="space-y-3">
            {costBreakdown.slice(0, 6).map(cat => {
              const barWidth = maxCost > 0 ? (cat.totalCost / maxCost) * 100 : 0;
              
              return (
                <div key={cat.key}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium truncate pr-2" title={cat.label}>
                      {cat.label}
                    </span>
                    <span className="text-sm font-semibold whitespace-nowrap">
                      {formatCurrency(cat.totalCost)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
                      <div 
                        className="h-full bg-gray-700 rounded transition-all duration-500"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground w-10 text-right">
                      {formatPercentInt(cat.pctOfTotal)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Chronic Conditions + Risk Distribution - Combined Card */}
        <div className="bg-card border rounded-lg p-4">
          {/* Chronic Condition Prevalence */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold uppercase text-sm tracking-wide">Chronic Condition Prevalence</h3>
            <span className="text-xs text-muted-foreground">Click to filter</span>
          </div>
          
          <table className="w-full text-sm mb-6">
            <tbody>
              {syncedConditions.map(cond => {
                const isSelected = activeFilter?.type === 'condition' && activeFilter.value === cond.label;
                
                return (
                  <tr 
                    key={cond.key} 
                    className={`border-b last:border-b-0 cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleConditionClick(cond.key, cond.label)}
                  >
                    <td className="py-2">
                      <span className="flex items-center gap-1.5">
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                        {cond.label}
                      </span>
                    </td>
                    <td className="py-2 text-right font-mono">{cond.patientCount} pts</td>
                    <td className="py-2 text-right font-mono w-16">{formatPercentInt(cond.pctOfPanel)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Risk Distribution */}
          <h3 className="font-semibold uppercase text-sm tracking-wide mb-3">Risk Distribution</h3>
          
          <table className="w-full text-sm">
            <tbody>
              {syncedRiskDistribution.map(r => {
                const isSelected = activeFilter?.type === 'risk' && activeFilter.value === r.key;
                
                return (
                  <tr 
                    key={r.key} 
                    className={`border-b last:border-b-0 cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleRiskClick(r.key, r.label)}
                  >
                    <td className="py-2">
                      <span className="flex items-center gap-2">
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                        <span className={`w-2.5 h-2.5 rounded-full ${
                          r.key === 'high' ? 'bg-red-500' :
                          r.key === 'medium' ? 'bg-amber-500' : 'bg-green-500'
                        }`} />
                        {r.label}
                      </span>
                    </td>
                    <td className="py-2 text-right font-mono">{r.patientCount} pts</td>
                    <td className="py-2 text-right font-mono w-16">{formatPercentInt(r.pctOfCost)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Utilization Patterns & Cost vs Risk Analysis - Side by Side */}
      <div className="grid grid-cols-2 gap-4">
        {/* Utilization Patterns */}
        <div className="bg-card border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold uppercase text-sm tracking-wide">Utilization Patterns</h3>
            
            {/* Toggle */}
            <div className="flex items-center gap-1 p-0.5 bg-slate-200 rounded-lg">
              <button
                onClick={() => setUtilizationView('patient')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  utilizationView === 'patient'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Patient
              </button>
              <button
                onClick={() => setUtilizationView('cost')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  utilizationView === 'cost'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Cost
              </button>
            </div>
          </div>
          
          {/* View Explanation */}
          <div className="text-xs text-muted-foreground mb-3 p-2 bg-muted/50 rounded">
            {utilizationView === 'cost' ? (
              <>Exclusive: Each patient counted once by highest-acuity service.</>
            ) : (
              <>Inclusive: Patients can appear in multiple categories.</>
            )}
            <span className="ml-1 text-blue-600">Click to filter ↓</span>
          </div>

          {utilizationView === 'cost' ? (
            <div className="space-y-1.5">
              {syncedUtilizationPatterns.map(pattern => {
                const barWidth = maxUtilCost > 0 ? (pattern.totalCost / maxUtilCost) * 100 : 0;
                const isSelected = utilizationFilter?.type === 'utilization' && utilizationFilter.value === pattern.key;
                
                return (
                  <div 
                    key={pattern.key}
                    className={`flex items-center gap-2 p-1.5 rounded cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleUtilizationClick(pattern.key, pattern.label)}
                  >
                    <div className="w-28 flex-shrink-0">
                      <span className="flex items-center gap-1 text-xs font-medium">
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                        {pattern.label}
                      </span>
                    </div>
                    <div className="w-14 text-right text-xs font-mono">{pattern.patientCount}</div>
                    <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                      <div className="h-full bg-gray-600 rounded" style={{ width: `${barWidth}%` }} />
                    </div>
                    <div className="w-12 text-right text-xs text-muted-foreground">
                      {formatPercentInt(pattern.pctOfSpend)}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-1.5">
              {syncedUtilizationFlags.map(flag => {
                const barWidth = flag.pctOfPanel;
                const isSelected = utilizationFilter?.type === 'utilizationFlag' && utilizationFilter.value === flag.key;
                
                return (
                  <div 
                    key={flag.key}
                    className={`flex items-center gap-2 p-1.5 rounded cursor-pointer transition-colors ${
                      isSelected ? 'bg-blue-50' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleUtilizationFlagClick(flag.key, flag.label)}
                  >
                    <div className="w-28 flex-shrink-0">
                      <span className="flex items-center gap-1 text-xs font-medium">
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />}
                        {flag.label}
                      </span>
                    </div>
                    <div className="w-14 text-right text-xs font-mono">{flag.patientCount}</div>
                    <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                      <div className="h-full bg-blue-500 rounded" style={{ width: `${barWidth}%` }} />
                    </div>
                    <div className="w-12 text-right text-xs text-muted-foreground">
                      {formatPercentInt(flag.pctOfPanel)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Cost vs Risk Quadrant */}
        <div className="bg-card border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="font-semibold uppercase text-sm tracking-wide">Cost vs Risk</h3>
              <p className="text-xs text-muted-foreground">
                Click quadrant to filter ↓
              </p>
            </div>
            {quadrantFilter && (
              <Badge 
                variant="secondary" 
                className="flex items-center gap-1.5 cursor-pointer hover:bg-secondary/80"
                onClick={clearQuadrantFilter}
              >
                {quadrantFilter.label}
                <X className="h-3 w-3" />
              </Badge>
            )}
          </div>

          {/* Quadrant Scatter Plot */}
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 20, bottom: 25, left: 40 }}>
                {/* Quadrant backgrounds */}
                <ReferenceArea x1={0} x2={riskThreshold} y1={costThreshold} y2={100} fill="#fee2e2" fillOpacity={0.5} />
                <ReferenceArea x1={riskThreshold} x2={100} y1={costThreshold} y2={100} fill="#fef3c7" fillOpacity={0.5} />
                <ReferenceArea x1={0} x2={riskThreshold} y1={0} y2={costThreshold} fill="#f3f4f6" fillOpacity={0.5} />
                <ReferenceArea x1={riskThreshold} x2={100} y1={0} y2={costThreshold} fill="#dcfce7" fillOpacity={0.5} />
                
                <ReferenceLine x={riskThreshold} stroke="#6B7280" strokeDasharray="5 5" strokeWidth={1.5} />
                <ReferenceLine y={costThreshold} stroke="#6B7280" strokeDasharray="5 5" strokeWidth={1.5} />
                
                <XAxis 
                  type="number" dataKey="riskPercentile" domain={[0, 100]}
                  ticks={[0, 50, 100]} tickFormatter={(v) => `${v}%`}
                  label={{ value: 'Risk %ile', position: 'bottom', offset: 5, fontSize: 10 }}
                  tick={{ fontSize: 9 }}
                />
                <YAxis 
                  type="number" dataKey="costPercentile" domain={[0, 100]}
                  ticks={[0, 50, 100]} tickFormatter={(v) => `${v}%`}
                  label={{ value: 'Cost %ile', angle: -90, position: 'insideLeft', offset: 5, fontSize: 10 }}
                  tick={{ fontSize: 9 }}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const p = payload[0].payload;
                    const quadrant = getQuadrant(p.riskPercentile, p.costPercentile);
                    return (
                      <div className="bg-white border rounded-lg shadow-lg p-2 text-xs">
                        <p className="font-semibold">{p.mbi}</p>
                        <p>Risk: {p.riskPercentile}th %ile</p>
                        <p>Cost: {formatCurrency(p.totalCost)}</p>
                        <p className="mt-1 pt-1 border-t">
                          {quadrant === 'opportunity' ? '🔴 Opportunity' :
                           quadrant === 'expected' ? '🟡 Expected' :
                           quadrant === 'wellManaged' ? '🟢 Well Managed' : '⚪ Efficient'}
                        </p>
                      </div>
                    );
                  }}
                />
                <Scatter 
                  name="Patients" data={scatterDataFiltered}
                  onClick={(data) => { if (data?.payload) setSelectedPatientMbi(data.payload.mbi); }}
                >
                  {scatterDataFiltered.map((p, idx) => {
                    const quadrant = getQuadrant(p.riskPercentile, p.costPercentile);
                    let color = '#9CA3AF';
                    if (quadrant === 'opportunity') color = '#EF4444';
                    else if (quadrant === 'expected') color = '#F59E0B';
                    else if (quadrant === 'wellManaged') color = '#22C55E';
                    return <Cell key={idx} fill={color} cursor="pointer" />;
                  })}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>

          {/* Quadrant Legend - Clickable */}
          <div className="grid grid-cols-4 gap-2 mt-3">
            <button
              onClick={() => handleQuadrantClick('opportunity', 'Low Risk / High Cost')}
              className={`flex items-center justify-between px-2 py-1.5 rounded text-xs transition-all border ${
                quadrantFilter?.value === 'opportunity'
                  ? 'bg-slate-100 border-slate-400 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-50'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-slate-700">Opportunity</span>
              </span>
              <span className="font-mono font-medium text-slate-900">{quadrantStats.opportunity.count}</span>
            </button>
            <button
              onClick={() => handleQuadrantClick('expected', 'High Risk / High Cost')}
              className={`flex items-center justify-between px-2 py-1.5 rounded text-xs transition-all border ${
                quadrantFilter?.value === 'expected'
                  ? 'bg-slate-100 border-slate-400 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-50'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-slate-700">Expected</span>
              </span>
              <span className="font-mono font-medium text-slate-900">{quadrantStats.expected.count}</span>
            </button>
            <button
              onClick={() => handleQuadrantClick('efficient', 'Low Risk / Low Cost')}
              className={`flex items-center justify-between px-2 py-1.5 rounded text-xs transition-all border ${
                quadrantFilter?.value === 'efficient'
                  ? 'bg-slate-100 border-slate-400 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-50'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-gray-400" />
                <span className="text-slate-700">Efficient</span>
              </span>
              <span className="font-mono font-medium text-slate-900">{quadrantStats.efficient.count}</span>
            </button>
            <button
              onClick={() => handleQuadrantClick('wellManaged', 'High Risk / Low Cost')}
              className={`flex items-center justify-between px-2 py-1.5 rounded text-xs transition-all border ${
                quadrantFilter?.value === 'wellManaged'
                  ? 'bg-slate-100 border-slate-400 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-50'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-slate-700">Well Managed</span>
              </span>
              <span className="font-mono font-medium text-slate-900">{quadrantStats.wellManaged.count}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Patient Table */}
      <div className="bg-card border rounded-lg p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold">Patients (sorted by cost)</h3>
              {activeFilter && (
                <Badge 
                  variant="secondary" 
                  className="flex items-center gap-1.5 cursor-pointer hover:bg-secondary/80"
                  onClick={clearFilter}
                >
                  {activeFilter.label}
                  <X className="h-3 w-3" />
                </Badge>
              )}
              {quadrantFilter && (
                <Badge 
                  variant="secondary" 
                  className="flex items-center gap-1.5 cursor-pointer hover:bg-secondary/80 bg-red-100 text-red-800 hover:bg-red-200"
                  onClick={clearQuadrantFilter}
                >
                  {quadrantFilter.label}
                  <X className="h-3 w-3" />
                </Badge>
              )}
              {utilizationFilter && (
                <Badge 
                  variant="secondary" 
                  className="flex items-center gap-1.5 cursor-pointer hover:bg-secondary/80 bg-blue-100 text-blue-800 hover:bg-blue-200"
                  onClick={clearUtilizationFilter}
                >
                  {utilizationFilter.label}
                  <X className="h-3 w-3" />
                </Badge>
              )}
              {(activeFilter || quadrantFilter || utilizationFilter) && (activeFilter && quadrantFilter || activeFilter && utilizationFilter || quadrantFilter && utilizationFilter) && (
                <button 
                  className="text-xs text-muted-foreground hover:text-foreground underline"
                  onClick={clearAllFilters}
                >
                  Clear all
                </button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {(activeFilter || quadrantFilter || utilizationFilter)
                ? `Showing ${filteredPatients.length} filtered patients`
                : `Showing ${displayedPatients.length} of ${highCostPatients.length} patients`
              }
            </p>
          </div>
          {filteredPatients.length > 10 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAllPatients(!showAllPatients)}
            >
              {showAllPatients ? 'Show Less' : 'Show All'}
            </Button>
          )}
        </div>

        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">MBI</th>
                <th className="text-center p-3 font-medium">Gender</th>
                <th className="text-center p-3 font-medium">DOB</th>
                <th className="text-center p-3 font-medium">Age</th>
                <th className="text-center p-3 font-medium">Deceased</th>
                <th className="text-right p-3 font-medium">HCC Risk</th>
                <th className="text-right p-3 font-medium">Total Cost</th>
                <th className="text-right p-3 font-medium">Nat'l Avg</th>
                <th className="text-right p-3 font-medium">Variance</th>
                <th className="text-center p-3 font-medium">Flags</th>
                <th className="text-center p-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedPatients.length > 0 ? (
                displayedPatients.map((patient, idx) => {
                  const age = calculateAge(patient.dob);
                  return (
                    <tr key={idx} className="border-t hover:bg-muted/30">
                      <td className="p-3 font-mono">{patient.mbi}</td>
                      <td className="p-3 text-center">{patient.gender || '—'}</td>
                      <td className="p-3 text-center text-sm">
                        {patient.dob ? new Date(patient.dob).toLocaleDateString() : '—'}
                      </td>
                      <td className="p-3 text-center">{age ?? '—'}</td>
                      <td className="p-3 text-center text-sm">
                        {patient.dateOfDeath ? new Date(patient.dateOfDeath).toLocaleDateString() : '—'}
                      </td>
                      <td className="p-3 text-right font-mono">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          patient.riskPercentile > 75 ? 'bg-red-100 text-red-800' :
                          patient.riskPercentile > 50 ? 'bg-amber-100 text-amber-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {patient.riskPercentile}%
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono">
                        {formatCurrency(patient.totalCost)}
                      </td>
                      <td className="p-3 text-right font-mono text-muted-foreground">
                        {formatCurrency(patient.nationalAvg)}
                      </td>
                      <td className={`p-3 text-right font-semibold ${
                        patient.variance > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {patient.variance > 0 ? '+' : ''}{formatPercent(patient.variance)}
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {patient.hasHospitalization && (
                            <Badge variant="destructive" className="text-xs px-1">IP</Badge>
                          )}
                          {patient.hasPostAcute && (
                            <Badge className="bg-amber-600 text-xs px-1">PAC</Badge>
                          )}
                          {patient.conditionCount >= 3 && (
                            <Badge variant="outline" className="text-xs px-1">3+</Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedPatientMbi(patient.mbi)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={11} className="p-8 text-center text-muted-foreground">
                    {(activeFilter || quadrantFilter || utilizationFilter) ? 'No patients match the current filters' : 'No patient data available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Patient Detail Modal */}
      <PatientDetailModal
        isOpen={!!selectedPatientMbi}
        onClose={() => setSelectedPatientMbi(null)}
        patientMbi={selectedPatientMbi}
        practiceId={practiceId}
        npi={npi}
      />
    </div>
  );
}

export default TPCCMeasureDetailPanel;
